import isPermissionEnabled from "common/isPermissionEnabled"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import {
  apexBarChartOptions,
  groupByForSales,
  paymentMethodTypes,
  salesByForSales,
} from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import React, { useEffect, useMemo, useRef, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import { getCategories } from "store/actions/categoriesAction"
import { getAllCityList } from "store/actions/cityAction"
import { getBranchCountryList } from "store/actions/commonAction"
import { getCountryList } from "store/actions/countriesAction"
import { getAllCustomersList } from "store/actions/customersAction"
import { getItemsListDropdown } from "store/actions/itemsAction"
import { getModifierList } from "store/actions/modiferAction"
import {
  getAllCouponsList,
  getAllPromotionsList,
} from "store/actions/promotionsAction"
import { getAllRegionList } from "store/actions/regionAction"
import { getAllSalesReports } from "store/actions/reportsAction"

const formatDateTime = date =>
  date
    .toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "")

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const SalesReports = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Sales Reports ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [isShowGraphBtn, setIsShowGraphBtn] = useState(true)
  const [showGraph, setShowGraph] = useState(false)
  const [customerOptions, setCustomerOptions] = useState([])
  const { branchsList, countryList } = useSelector(state => state.Common)
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    salesReports,
  } = useSelector(state => state.Reports)

  const { regionList } = useSelector(state => state.Region)
  const { cityList } = useSelector(state => state.City)
  const { promotionsList, couponsLists } = useSelector(
    state => state.Promotions
  )
  const { allCategories } = useSelector(state => state.Categories)
  const { itemsListDropdown } = useSelector(state => state.Items)
  const { customersDropdownList } = useSelector(state => state.Customers)
  const { modifierLists } = useSelector(state => state.Modifiers)

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const flatpickrRef = useRef(null)
  const dropdownRef = useRef(null)

  const [options, setOptions] = useState(apexBarChartOptions)
  const [series, setSeries] = useState()
  const [graphMetricType, setGraphMetricType] = useState("totalAmount")

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      16,
      "view_sales_reports",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
    })
  }, [userRole])

  useEffect(() => {
    fetchSalesReports()
    fetchBranchCountryList()
  }, [])

  const getColNameBySalesBy = salesBy => {
    const selectedOption = salesByForSales.find(
      option => option.value === salesBy
    )
    return selectedOption
      ? language === "sa"
        ? selectedOption.colNameAr
        : selectedOption.colName
      : ""
  }

  const fetchBranchCountryList = () => {
    dispatch(getBranchCountryList())
  }

  const fetchSalesReports = () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      salesBy: "branch",
      groupBy: "",
      branchId: [],
      customerId: [],
      couponId: [],
      promotionId: [],
      cityId: [],
      regionId: [],
      countryId: [],
      orderType: [],
      categoryId: [],
      modifierId: [],
      platform: [],
      paymentMethod: [],
      itemId: [],
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
    }
    dispatch(getAllSalesReports(payload))
    setIsApiCalled(true)
  }

  const fetchRegionList = countryId => {
    dispatch(getAllRegionList(countryId))
  }

  const fetchCityList = (countryId, regionId) => {
    const payload = {
      countryId,
      regionId,
    }
    dispatch(getAllCityList(payload))
  }

  const fetchPromotionsList = () => {
    dispatch(getAllPromotionsList())
  }

  const fetchCouponsList = () => {
    dispatch(getAllCouponsList())
  }

  const fetchCategoriesList = () => {
    dispatch(getCategories({ isCombo: false }))
  }

  const fetchItemsList = () => {
    dispatch(getItemsListDropdown())
  }

  const fetchModifierList = () => {
    dispatch(getModifierList())
  }

  const fetchCustomersList = keyword => {
    const payload = {
      keyword,
    }
    dispatch(getAllCustomersList(payload))
  }

  const [selectedFilters, setSelectedFilters] = useState({
    branchId: [],
    groupBy: "",
    salesBy: {
      label: t("Branch"),
      value: "branch",
    },
    countryId: [],
    regionId: [],
    cityId: [],
    customerId: [],
    couponId: [],
    promotionId: [],
    orderType: [],
    categoryId: [],
    modifierId: [],
    platform: [],
    paymentMethod: [],
    itemId: [],
  })
  // Columns / title / graph follow last applied search/reset, not draft filter changes
  const [appliedFilters, setAppliedFilters] = useState({
    branchId: [],
    groupBy: "",
    salesBy: {
      label: t("Branch"),
      value: "branch",
    },
    countryId: [],
    regionId: [],
    cityId: [],
    customerId: [],
    couponId: [],
    promotionId: [],
    orderType: [],
    categoryId: [],
    modifierId: [],
    platform: [],
    paymentMethod: [],
    itemId: [],
  })

  const formatPlatformLabel = value => {
    if (value == null || value === "") return value ?? ""
    const platform = String(value)
    if (platform === "android" || platform === "Android") return t("Android")
    if (platform === "website" || platform === "Website") return t("Website")
    if (platform === "iOS" || platform === "ios" || platform === "IOS") {
      return t("iOS")
    }
    return t(platform)
  }

  useEffect(() => {
    const { salesBy } = appliedFilters

    if (salesReports) {
      const colName = getColNameBySalesBy(salesBy.value)
      const updatedCategories = salesReports?.map(item =>
        colName === "platform"
          ? formatPlatformLabel(item[colName])
          : item[colName]
      )
      const updatedSeries = salesReports?.map(item => item[graphMetricType])

      const dataSeries = [
        {
          data: updatedSeries,
        },
      ]

      setSeries(dataSeries)

      setOptions(prev => ({
        ...prev,
        xaxis: {
          categories: updatedCategories,
        },
      }))
    }
  }, [salesReports, graphMetricType, language, appliedFilters?.salesBy?.value])

  useEffect(() => {
    if (customersDropdownList.length > 0) {
      const updatedCustomerOption = customersDropdownList?.map(item => ({
        label: `${item.firstName} ${item.lastName}, ${
          item?.phoneNumber ? item?.phoneNumber : ""
        }`,
        value: item._id,
      }))
      setCustomerOptions(updatedCustomerOption)
    }
  }, [customersDropdownList])

  const filters = [
    {
      name: "salesBy",
      type: "singleSelect",
      options: [
        ...salesByForSales.map(item => ({
          label: t(item.label),
          value: item.value,
        })),
      ],
      placeholder: t("Select Sales by"),
    },
    {
      name: "groupBy",
      type: "singleSelect",
      options: [
        ...groupByForSales.map(item => ({
          label: t(item.label),
          value: item.value,
        })),
      ],
      placeholder: t("Select Group By"),
    },
    {
      name: "countryId",
      type: "singleSelect",
      options: [
        ...countryList.map(item => ({
          label: language === "sa" ? item.nameAr : item.name,
          value: item._id,
        })),
      ],
      placeholder: t("Select Country"),
    },
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branch"),
    },
  ]

  // ADD REGION DROPDOWN
  if (Object.keys(selectedFilters.countryId).length > 0) {
    filters.push({
      name: "regionId",
      type: "singleSelect",
      options: regionList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Region"),
    })
  }

  // ADD CITY DROPDOWN
  if (Object.keys(selectedFilters.regionId).length > 0) {
    filters.push({
      name: "cityId",
      type: "singleSelect",
      options: cityList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select City"),
    })
  }

  if (selectedFilters?.salesBy?.value === "orderType") {
    filters.push({
      name: "orderType",
      type: "multiSelect",
      options: [
        // {
        //   label: t("Curbside"),
        //   value: "curbside",
        // },
        {
          label: t("Pickup"),
          value: "pickup",
        },
        {
          label: t("Delivery"),
          value: "delivery"
        },
      ],
      placeholder: t("Select Order Type"),
    })
  }

  if (selectedFilters?.salesBy?.value === "platform") {
    filters.push({
      name: "platform",
      type: "multiSelect",
      options: [
        {
          label: t("iOS"),
          value: "iOS",
        },
        {
          label: t("Android"),
          value: "android",
        },
        {
          label: t("Website"),
          value: "website",
        },
      ],
      placeholder: t("Select Platform"),
    })
  }

  if (selectedFilters?.salesBy?.value === "paymentMethod") {
    filters.push({
      name: "paymentMethod",
      type: "multiSelect",
      options: paymentMethodTypes.map(item => ({
        label: t(item.label),
        value: item.value,
      })),
      placeholder: t("Select Payment Method"),
    })
  }

  if (selectedFilters?.salesBy?.value === "promotion") {
    filters.push({
      name: "promotionId",
      type: "multiSelect",
      options: promotionsList.map(item => ({
        label: item.name,
        value: item._id,
      })),
      placeholder: t("Select Discounts"),
    })
  }

  if (selectedFilters?.salesBy?.value === "coupon") {
    filters.push({
      name: "couponId",
      type: "multiSelect",
      options: couponsLists.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Coupons"),
    })
  }

  if (selectedFilters?.salesBy?.value === "category") {
    filters.push({
      name: "categoryId",
      type: "multiSelect",
      options: allCategories.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Categories"),
    })
  }

  if (selectedFilters?.salesBy?.value === "item") {
    filters.push({
      name: "itemId",
      type: "multiSelect",
      options: itemsListDropdown.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Items"),
    })
  }

  if (selectedFilters?.salesBy?.value === "customer") {
    filters.push({
      name: "customerId",
      type: "multiSelect",
      options: customerOptions.map(item => ({
        label: item.label,
        value: item.value,
      })),
      placeholder: t("Type To Search Customer"),
    })
  }

  if (selectedFilters?.salesBy?.value === "modifier") {
    filters.push({
      name: "modifierId",
      type: "multiSelect",
      options: modifierLists.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Modifiers"),
    })
  }

  const generatePayload = page => {
    const {
      salesBy,
      branchId,
      groupBy,
      countryId,
      regionId,
      cityId,
      orderType,
      platform,
      paymentMethod,
      promotionId,
      couponId,
      categoryId,
      itemId,
      customerId,
      modifierId,
    } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const groupByValue = groupBy.value || ""
    const salesByValue = salesBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const orderTypeIds =
      salesBy.value === "orderType" ? orderType?.map(item => item.value) : []
    const platformIds =
      salesBy.value === "platform"
        ? platform?.flatMap(item => {
            if (item.value === "android") return ["android", "Android"]
            if (item.value === "website") return ["website", "Website"]
            return [item.value]
          }) || []
        : []
    const paymentMethodIds =
      salesBy.value === "paymentMethod"
        ? paymentMethod?.map(item => item.value)
        : []
    const promotionIds =
      salesBy.value === "promotion" ? promotionId?.map(item => item.value) : []
    const couponIds =
      salesBy.value === "coupon" ? couponId?.map(item => item.value) : []
    const categoryIds =
      salesBy.value === "category" ? categoryId?.map(item => item.value) : []
    const itemIds =
      salesBy.value === "item" ? itemId?.map(item => item.value) : []
    const customerIds =
      salesBy.value === "customer" ? customerId?.map(item => item.value) : []
    const modifierIds =
      salesBy.value === "modifier" ? modifierId?.map(item => item.value) : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      branchId: branchIds,
      groupBy: groupByValue,
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
      salesBy: salesByValue,
      customerId: customerIds,
      couponId: couponIds,
      promotionId: promotionIds,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
      orderType: orderTypeIds,
      categoryId: categoryIds,
      modifierId: modifierIds,
      platform: platformIds,
      paymentMethod: paymentMethodIds,
      itemId: itemIds,
    }

    return payload
  }

  const handleSearch = () => {
    const { groupBy } = selectedFilters
    const payload = generatePayload(1)

    setAppliedFilters(selectedFilters)
    setSelectedColumns(getColumns(selectedFilters)?.map(column => column.key))

    // HIDE GRAPH WHENEVER APPLY FILTER
    setShowGraph(false)

    if (groupBy === "" || groupBy.value === "") {
      setIsShowGraphBtn(true)
    } else {
      setIsShowGraphBtn(false)
      setShowGraph(false)
    }

    dispatch(getAllSalesReports(payload))
    navigate(`/sales-reports?page=${1}`)
  }

  const handleReset = () => {
    const defaultDateRange = getDefaultDateRange()
    const resetFilters = {
      branchId: [],
      groupBy: "",
      salesBy: {
        label: t("Branch"),
        value: "branch",
      },
      countryId: [],
      regionId: [],
      cityId: [],
      customerId: [],
      couponId: [],
      promotionId: [],
      orderType: [],
      categoryId: [],
      modifierId: [],
      platform: [],
      paymentMethod: [],
      itemId: [],
    }
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      salesBy: "branch",
      groupBy: "",
      branchId: [],
      customerId: [],
      couponId: [],
      promotionId: [],
      cityId: [],
      regionId: [],
      countryId: [],
      orderType: [],
      categoryId: [],
      modifierId: [],
      platform: [],
      paymentMethod: [],
      itemId: [],
      dateRange: defaultDateRange,
    }
    dispatch(getAllSalesReports(payload))
    navigate(`/sales-reports?page=${1}`)
    setSelectedFilters(prev => ({
      ...prev,
      ...resetFilters,
    }))
    setAppliedFilters(resetFilters)
    // Keep visible columns in sync with reset salesBy (e.g. item -> branch),
    // otherwise only shared keys like totalCustomer remain selected.
    setSelectedColumns(getColumns(resetFilters)?.map(column => column.key))
    setIsShowGraphBtn(true)
    setShowGraph(false)
    setDateRange(defaultDateRange)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [defaultDateRange.min, defaultDateRange.max],
        true
      )
    }
  }

  const handleFilterChange = (selectedValue, filterName) => {
    const value = selectedValue.value

    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
    if (filterName === "countryId") {
      // GET REGION LIST
      fetchRegionList(value)
      setSelectedFilters(prev => ({
        ...prev,
        regionId: [],
        cityId: [],
      }))
    } else if (filterName === "regionId") {
      // GET CITIES LIST
      const { countryId } = selectedFilters
      fetchCityList(countryId.value, value)
      setSelectedFilters(prev => ({
        ...prev,
        cityId: [],
      }))
    } else if (value === "promotion") {
      fetchPromotionsList()
    } else if (value === "coupon") {
      fetchCouponsList()
    } else if (value === "category") {
      fetchCategoriesList()
    } else if (value === "item") {
      fetchItemsList()
    } else if (value === "modifier") {
      fetchModifierList()
    }
  }

  const onInputChange = value => {
    fetchCustomersList(value)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters, language])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const onPageChange = page => {
    const payload = generatePayload(page)

    dispatch(getAllSalesReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/sales-reports?page=${page}`)
        }
      })
      .catch(error => {})
  }

  const getColumns = selectedFilters => {
    const salesByValue = selectedFilters?.salesBy?.value
    const groupByValue = selectedFilters?.groupBy?.value

    const isItemOrModifier =
      salesByValue === "item" ||
      salesByValue === "modifier" ||
      groupByValue === "item" ||
      groupByValue === "modifier"

    const baseColumns = [
      ...(isItemOrModifier
        ? []
        : [
            {
              key: "totalAmount",
              label: t("Total Sales"),
              format: formatCurrency,
              isShowInGraph: true,
            },
            {
              key: "totalCount",
              label: t("Total Orders"),
              format: formatNumber,
              isShowInGraph: true,
            },
            {
              key: "discountAmount",
              label: t("Discount"),
              format: formatCurrency,
              isShowInGraph: true,
            },
            {
              key: "taxAmount",
              label: t("Tax"),
              format: formatCurrency,
              isShowInGraph: true,
            },
            {
              key: "amountWithoutTax",
              label: t("Amount Without Tax"),
              format: formatCurrency,
              isShowInGraph: true,
            },
            {
              key: "averageOrderAmount",
              label: t("Average Order Amount"),
              format: formatCurrency,
              isShowInGraph: true,
            },
          ]),
    ]

    // FOR groupBy
    if (
      selectedFilters?.salesBy?.value === "branch" ||
      selectedFilters?.groupBy?.value === "branch"
    ) {
      baseColumns.splice(0, 0, {
        key: language === "sa" ? "branchNameAr" : "branchName",
        label: t("Branch Name"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "category" ||
      selectedFilters?.salesBy?.value === "category"
    ) {
      baseColumns.splice(0, 0, {
        key: language === "sa" ? "categoryNameAr" : "categoryName",
        label: t("Category Name"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "item" ||
      selectedFilters?.salesBy?.value === "item"
    ) {
      baseColumns.splice(
        0,
        0,
        {
          key: language === "sa" ? "itemNameAr" : "itemName",
          label: t("Item Name"),
          readOnly: true,
        },

        { key: "totalItemQuantityCount", label: t("Item Count") },
        {
          key: "totalItemAmount",
          label: t("Total Item Amount"),
          format: formatNumber,
        }
      )
    }
    if (
      selectedFilters?.groupBy?.value === "modifier" ||
      selectedFilters?.salesBy?.value === "modifier"
    ) {
      baseColumns.splice(
        0,
        0,
        {
          key: language === "sa" ? "modifierNameAr" : "modifierName",
          label: t("Modifier Name"),
          readOnly: true,
        },
        { key: "totalItemQuantityCount", label: t("Modifier Count") },
        {
          key: "totalModifierAmount",
          label: t("Total Modifier Amount"),
          format: formatNumber,
        }
      )
    }
    if (
      selectedFilters?.groupBy?.value === "orderType" ||
      selectedFilters?.salesBy?.value === "orderType"
    ) {
      baseColumns.splice(0, 0, {
        key: "orderType",
        label: t("Order Type"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "customer" ||
      selectedFilters?.salesBy?.value === "customer"
    ) {
      baseColumns.splice(
        0,
        0,
        { key: "customerName", label: t("Customer Name"), readOnly: true },
        // { key: "customerEmail", label: "Customer Email" },
        { key: "customerPhoneNumber", label: t("Customer Phone Number") }
      )
    }
    if (
      selectedFilters?.groupBy?.value === "coupon" ||
      selectedFilters?.salesBy?.value === "coupon"
    ) {
      baseColumns.splice(
        0,
        0,
        {
          key: language === "sa" ? "couponNameAr" : "couponName",
          label: t("Coupon Name"),
          readOnly: true,
        },
        { key: "couponCode", label: t("Coupon Code") }
      )
    }
    if (
      selectedFilters?.groupBy?.value === "platform" ||
      selectedFilters?.salesBy?.value === "platform"
    ) {
      baseColumns.splice(0, 0, {
        key: "platform",
        label: t("Platform"),
        readOnly: true,
        format: formatPlatformLabel,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "paymentMethod" ||
      selectedFilters?.salesBy?.value === "paymentMethod"
    ) {
      baseColumns.splice(0, 0, {
        key: "paymentMethod",
        label: t("Payment Method"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "promotion" ||
      selectedFilters?.salesBy?.value === "promotion"
    ) {
      baseColumns.splice(0, 0, {
        key: "promotionName",
        label: t("Discount Name"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "city" ||
      selectedFilters?.salesBy?.value === "city"
    ) {
      baseColumns.splice(0, 0, {
        key: language === "sa" ? "cityNameAr" : "cityName",
        label: t("City Name"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "region" ||
      selectedFilters?.salesBy?.value === "region"
    ) {
      baseColumns.splice(0, 0, {
        key: language === "sa" ? "regionNameAr" : "regionName",
        label: t("Region Name"),
        readOnly: true,
      })
    }
    if (
      selectedFilters?.groupBy?.value === "country" ||
      selectedFilters?.salesBy?.value === "country"
    ) {
      baseColumns.splice(0, 0, {
        key: language === "sa" ? "countryNameAr" : "countryName",
        label: t("Country Name"),
        readOnly: true,
      })
    }
    if (selectedFilters?.groupBy?.value === "date") {
      baseColumns.splice(1, 0, {
        key: "date",
        label: t("Date"),
      })
    }
    if (selectedFilters?.groupBy?.value === "hour") {
      baseColumns.splice(1, 0, {
        key: "hour",
        label: t("Hour"),
      })
    }
    if (selectedFilters?.groupBy?.value === "month") {
      baseColumns.splice(
        1,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "month",
          label: t("Month"),
        }
      )
    }
    if (selectedFilters?.groupBy?.value === "year") {
      baseColumns.splice(1, 0, {
        key: "year",
        label: t("Year"),
      })
    }
    if (selectedFilters?.groupBy?.value === "quarter") {
      baseColumns.splice(
        2,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "quarter",
          label: t("Quarter"),
        }
      )
    }
    if (selectedFilters?.groupBy?.value === "half") {
      baseColumns.splice(
        1,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "half",
          label: t("Half"),
        }
      )
    }
    if (selectedFilters?.groupBy?.value === "dayOfMonth") {
      baseColumns.splice(
        1,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "month",
          label: t("Month"),
        },
        {
          key: "day",
          label: t("Day"),
        }
      )
    }
    if (selectedFilters?.groupBy?.value === "week") {
      baseColumns.splice(
        1,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "week",
          label: t("Week"),
        }
      )
    }
    if (selectedFilters?.groupBy?.value === "dayOfYear") {
      baseColumns.splice(
        1,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "dayOfYear",
          label: t("Day Of Year"),
        }
      )
    }
    if (selectedFilters?.groupBy?.value === "dayOfWeek") {
      baseColumns.splice(
        1,
        0,
        {
          key: "year",
          label: t("Year"),
        },
        {
          key: "dayOfWeek",
          label: t("Day Of Week"),
        }
      )
    }

    // Add "totalCustomer" column if salesBy.value is not "customer"
    if (
      selectedFilters?.salesBy?.value !== "customer" &&
      selectedFilters?.groupBy?.value !== "customer"
    ) {
      baseColumns.push({
        key: "totalCustomer",
        label: t("Total Customers"),
        format: formatNumber,
      })
    }

    return baseColumns
  }

  const formatCurrency = (value, currencyCode) => {
    const amount = value ?? ""
    const translatedCurrency = currencyCode ? t(currencyCode) : ""
    return `${amount} ${translatedCurrency}`.trim()
  }

  const getCurrencyCode = countryCode => {
    const country = countryList.find(item => item.countryCode === countryCode)
    return country ? country.currency : "-" // Default to USD if not found
  }

  const formatNumber = value => {
    return value?.toLocaleString()
  }

  const generateExportPayload = (type, exportType) => {
    const {
      salesBy,
      branchId,
      groupBy,
      countryId,
      regionId,
      cityId,
      orderType,
      platform,
      paymentMethod,
      promotionId,
      couponId,
      categoryId,
      itemId,
      customerId,
      modifierId,
    } = selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const groupByValue = groupBy.value || ""
    const salesByValue = salesBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const orderTypeIds =
      salesBy.value === "orderType" ? orderType?.map(item => item.value) : []
    const platformIds =
      salesBy.value === "platform"
        ? platform?.flatMap(item => {
            if (item.value === "android") return ["android", "Android"]
            if (item.value === "website") return ["website", "Website"]
            return [item.value]
          }) || []
        : []
    const paymentMethodIds =
      salesBy.value === "paymentMethod"
        ? paymentMethod?.map(item => item.value)
        : []
    const promotionIds =
      salesBy.value === "promotion" ? promotionId?.map(item => item.value) : []
    const couponIds =
      salesBy.value === "coupon" ? couponId?.map(item => item.value) : []
    const categoryIds =
      salesBy.value === "category" ? categoryId?.map(item => item.value) : []
    const itemIds =
      salesBy.value === "item" ? itemId?.map(item => item.value) : []
    const customerIds =
      salesBy.value === "customer" ? customerId?.map(item => item.value) : []
    const modifierIds =
      salesBy.value === "modifier" ? modifierId?.map(item => item.value) : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type,
      exportType,
      branchId: branchIds,
      groupBy: groupByValue,
      salesBy: salesByValue,
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
      salesBy: salesByValue,
      customerId: customerIds,
      couponId: couponIds,
      promotionId: promotionIds,
      cityId: cityValue,
      regionId: regionValue,
      countryId: countryValue,
      orderType: orderTypeIds,
      categoryId: categoryIds,
      modifierId: modifierIds,
      platform: platformIds,
      paymentMethod: paymentMethodIds,
      itemId: itemIds,
    }

    return payload
  }

  const exportToCSV = async () => {
    const payload = generateExportPayload("REPORT-SALES", "csv")

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const hasDateRange = dateRange?.min && dateRange?.max
      const fileName = hasDateRange
        ? t(`sales_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Sales Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const payload = generateExportPayload("REPORT-SALES", "xlsx")

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const hasDateRange = dateRange?.min && dateRange?.max
      const fileName = hasDateRange
        ? t(`sales_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Sales Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState(
    getColumns(appliedFilters)?.map(column => column.key)
  )

  const columns = getColumns(appliedFilters)?.map(column => ({
    value: column.key,
    name: column.label,
    readOnly: column.readOnly,
  }))

  const handleColumnSelect = (e, columnKey) => {
    e.stopPropagation()

    setSelectedColumns(prevSelectedColumns => {
      if (prevSelectedColumns.includes(columnKey)) {
        return prevSelectedColumns.filter(col => col !== columnKey)
      }
      return [...prevSelectedColumns, columnKey]
    })
  }

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown)
  }

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            {showGraph && (
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between">
                    <CardTitle>{t("Sales Report Graph")}</CardTitle>

                    <select
                      name=""
                      id=""
                      value={graphMetricType}
                      onChange={e => {
                        setGraphMetricType(e.target.value)
                      }}
                      style={{
                        background: "transparent",
                        border: "1px solid #495057",
                        borderRadius: 5,
                        padding: 5,
                      }}
                    >
                      {getColumns(appliedFilters)
                        ?.filter(column => column.isShowInGraph)
                        ?.map(column => (
                          <option key={column.key} value={column.key}>
                            {column.label}
                          </option>
                        ))}
                    </select>
                  </div>
                  {loading ? null : (
                    <div style={{ direction: "ltr" }}>
                      <ReactApexChart
                        options={options}
                        series={series}
                        type="bar"
                        height="350"
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
            <Card>
              <DataExportDropdown
                cardTitle={`${t("Sales By")} ${
                  appliedFilters?.salesBy?.label
                }`}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isAddBtn={loading ? false : isShowGraphBtn}
                addBtnText={`${showGraph ? t("Hide") : t("Show")} ${t(
                  "Graph"
                )}`}
                onAddBtnClick={() => setShowGraph(!showGraph)}
                isExportData={loading ? false : true}
                isCustomColumn={loading ? false : true}
                showDropdown={showDropdown}
                columns={columns}
                dropdownRef={dropdownRef}
                selectedColumns={selectedColumns}
                handleColumnSelect={handleColumnSelect}
                handleDropdownToggle={handleDropdownToggle}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  setDateRange={setDateRange}
                  colSize={4}
                  onInputChange={onInputChange}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : salesReports.length === 0 ? (
                    <p className="text-center my-3">
                      {t("No sales reports found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {getColumns(appliedFilters)
                            ?.filter(column =>
                              selectedColumns.includes(column.key)
                            )
                            ?.map(column => (
                              <th key={column.key}>{column.label}</th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {salesReports.map((report, index) => (
                          <tr key={index} className="capitalize_td">
                            {getColumns(appliedFilters)
                              ?.filter(column =>
                                selectedColumns.includes(column.key)
                              )
                              ?.map(column => (
                                <td key={`${index}-${column.key}`}>
                                  {column.format
                                    ? column.format(
                                        report[column.key],
                                        getCurrencyCode(report.countryCode)
                                      )
                                    : report[column.key]}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                      {/* <tfoot>
                        <tr>
                          {getColumns(appliedFilters)
                            ?.filter(column =>
                              selectedColumns.includes(column.key)
                            )
                            ?.map(column => (
                              <td key={`total-${column.key}`}>
                                {[
                                  "totalAmount",
                                  "totalCount",
                                  "discountAmount",
                                  "taxAmount",
                                  "amountWithoutTax",
                                  "averageOrderAmount",
                                  "totalCustomer",
                                  "totalItemQuantityCount",
                                  "totalModifierAmount",
                                  "totalItemAmount",
                                ].includes(column.key)
                                  ? column?.format
                                    ? column?.format(
                                        salesReports?.reduce(
                                          (total, report) =>
                                            total + report[column.key],
                                          0
                                        )
                                      )
                                    : salesReports?.reduce(
                                        (total, report) =>
                                          total + report[column.key],
                                        0
                                      )
                                  : ""}
                              </td>
                            ))}
                        </tr>
                      </tfoot> */}
                    </Table>
                  )}
                </div>

                {!loading && salesReports.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={salesReports}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default SalesReports
